import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, GraduationCap, CheckCircle, Brain, Calendar, Sparkles } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";


const sections = [
  { id: "challenge", title: "The Medical School Challenge" },
  { id: "ai-tools", title: "How AI Helps Medical Students" },
  { id: "strategy", title: "The AI-Powered Study Strategy" },
  { id: "subjects", title: "Subject-Specific Tips" },
  { id: "schedule", title: "Sample Weekly Schedule" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

export default function MedicalStudyGuide() {
  useSEO({
    title: "Studying for Medical School with AI: The Complete Guide | System Atlas",
    description: "How to use AI tools to study for medical school (PASS/PACES/USMLE). Subject-specific strategies, study schedules, and proven retention techniques.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/medical-study-ai" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/medical-study-ai" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/medical-study-ai" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="Studying for Medical School with AI: The Complete Guide" description="How to use AI tools to study for medical school." publishedDate="2026-02-23" modifiedDate="2026-02-23" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8"><ArrowLeft className="w-4 h-4" />Back to guides</Link>
        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">Studying for Medical School with AI: The Complete Guide</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Medical school demands mastering an enormous volume of material in limited time. AI tools can transform how you process, structure, and retain medical knowledge. if used strategically.</p>
          <ArticleTOC sections={sections} />

          <section id="challenge" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" />The Medical School Challenge</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">Medical programs (PASS in France, pre-clinical years in the US/UK) have a unique challenge: the sheer volume. You're expected to learn anatomy, physiology, biochemistry, pharmacology, and more. often simultaneously.</p>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li>• <strong className="text-foreground">Volume:</strong> 300+ pages of material per week in some programs</li>
              <li>• <strong className="text-foreground">Precision:</strong> Details matter. drug dosages, anatomical landmarks, metabolic pathways</li>
              <li>• <strong className="text-foreground">Integration:</strong> You need to connect concepts across subjects (anatomy + physiology + pathology)</li>
              <li>• <strong className="text-foreground">Time pressure:</strong> Exams cover months of material with high pass thresholds</li>
            </ul>
          </section>

          <section id="ai-tools" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />How AI Helps Medical Students</h2>
            <div className="space-y-4">
              {[
                { t: "Rapid sheet generation", d: "Upload a 50-page PDF of your anatomy lectures and get a structured revision sheet in minutes. Focus your time on understanding, not note-taking." },
                { t: "Adaptive quizzing", d: "AI-generated MCQs that adapt to your level. Get harder questions on topics you know, easier ones on weak areas." },
                { t: "Gap analysis", d: "After each quiz, AI identifies exactly which concepts you're weak on. No more guessing what to study next." },
                { t: "Ask about YOUR materials", d: "Instead of searching Google, ask questions directly about your own lecture content. The answers are guaranteed to match your syllabus." },
              ].map(i => (
                <div key={i.t} className="p-4 border border-border/60 rounded-lg">
                  <h3 className="font-medium mb-2">{i.t}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{i.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="strategy" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Brain className="w-5 h-5 text-primary" />The AI-Powered Study Strategy</h2>
            <div className="space-y-3">
              {[
                { n: "1", t: "Day of lecture: Upload and generate", d: "Upload your lecture slides/notes immediately. Generate a revision sheet while the material is fresh." },
                { n: "2", t: "Same evening: First review + quiz", d: "Read the sheet and take a 10-question quiz. This first review within 24 hours dramatically improves retention." },
                { n: "3", t: "Day 3: Active recall session", d: "Close the sheet and try to explain key concepts from memory. Use the AI chat to check your understanding." },
                { n: "4", t: "Day 7: Comprehensive quiz", d: "Take a full quiz covering all the week's material. Use gap analysis to identify weak spots." },
                { n: "5", t: "Pre-exam: Focused revision", d: "Use spaced repetition scheduling to review sheets at optimal intervals. Focus extra time on flagged weak topics." },
              ].map(i => (
                <div key={i.n} className="p-4 border-l-4 border-primary bg-primary/5 rounded-r-lg">
                  <h3 className="font-medium mb-1 text-sm">{i.t}</h3>
                  <p className="text-xs text-muted-foreground">{i.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="subjects" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Subject-Specific Tips</h2>
            <div className="space-y-4">
              {[
                { t: "Anatomy", tips: ["Use visual descriptions in sheets. describe spatial relationships", "Quiz yourself on muscle origins/insertions systematically", "Cross-reference with clinical applications"] },
                { t: "Biochemistry", tips: ["Focus sheets on metabolic pathways with key enzymes", "Include regulation points and clinical significance", "Use the AI to generate pathway comparison tables"] },
                { t: "Pharmacology", tips: ["Structure sheets by drug class, not individual drugs", "Include mechanism, indication, side effects, and interactions", "Quiz on drug interactions. the most commonly tested topic"] },
                { t: "Physiology", tips: ["Use diagrams and flowcharts in your sheets", "Focus on regulatory mechanisms and feedback loops", "Connect normal physiology to pathological states"] },
              ].map(s => (
                <div key={s.t} className="p-4 border border-border/60 rounded-lg">
                  <h3 className="font-medium mb-2 text-sm">{s.t}</h3>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {s.tips.map(tip => <li key={tip} className="flex items-start gap-1.5"><CheckCircle className="w-3 h-3 text-primary mt-0.5 shrink-0" />{tip}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section id="schedule" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" />Sample Weekly Schedule</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border/60 rounded-lg">
                <thead>
                  <tr className="bg-muted/30">
                    <th className="p-3 text-left font-medium">Day</th>
                    <th className="p-3 text-left font-medium">Activity</th>
                    <th className="p-3 text-left font-medium">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {[
                    ["Mon-Fri", "Upload lecture → Generate sheet → First review", "30 min/lecture"],
                    ["Tue, Thu", "Quiz on Day-3 material (active recall)", "20 min"],
                    ["Saturday", "Weekly comprehensive quiz + gap analysis", "90 min"],
                    ["Sunday", "Spaced review of flagged weak topics", "60 min"],
                  ].map(([day, act, dur]) => (
                    <tr key={day} className="border-t border-border/30">
                      <td className="p-3 font-medium text-foreground">{day}</td>
                      <td className="p-3">{act}</td>
                      <td className="p-3">{dur}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Upload materials the same day as the lecture. don't let them pile up</li>
              <li>• First review within 24 hours is critical for retention</li>
              <li>• Use quizzes actively, not just for testing. they're a learning tool</li>
              <li>• Gap analysis tells you exactly what to focus on</li>
              <li>• Connect subjects: anatomy + physiology + pathology for each system</li>
              <li>• Spaced repetition is non-negotiable for the volume of medical school</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/ai-study-law" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Study Law with AI</h4>
              <p className="text-xs text-muted-foreground">AI methods for law students</p>
            </Link>
            <Link to="/learn/ai-study-engineering" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">AI for Engineering Students</h4>
              <p className="text-xs text-muted-foreground">Study methods for STEM with AI</p>
            </Link>
            <Link to="/learn/spaced-repetition" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Spaced Repetition</h4>
              <p className="text-xs text-muted-foreground">The science of long-term memory</p>
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
}
